import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { orderActions } from '@entities/Order';
import { schemaFormOrder, schemaFullFormOrder } from '@helpers/validationForm';
import { useTireInCart } from '@hooks/useTireInCart';
import { selectRegion } from '@redux/slices/header';
import { FormField } from '@app/shared';
import { CheckBox, CheckBoxAgree, CheckBoxReceiveNotifications } from '@ui-kit/MyCheckBox';
import { Button } from '@ui-kit/MyButton';
import { Region } from '@enums/RegionList';
import { RegionTypes } from '@app/Types/Region';

import styles from './FormOrder.module.scss';

export const FormOrder = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const shortRegion = useAppSelector(state => state.header.data.regionPartner);
  const { REGION_TYPE: typeRegion, REGION: regionId } = useAppSelector(selectRegion);
  const isShowCheckboxPackages = useTireInCart();
  const infoUser = useAppSelector(state => state.order.data.model.infoUser);
  const delivery = useAppSelector(state => state.order.data.model.delivery);
  const [legalEntity, setLegalEntity] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset,
  } = useForm({
    resolver: legalEntity ? yupResolver(schemaFullFormOrder) : yupResolver(schemaFormOrder),
    mode: 'onChange',
    // TODO подделать поля что бы было полное совпадение с моделью
    defaultValues: {
      ...infoUser,
      // Checkbox
      legalEntity: false,
      consultation: false,
      record: false,
      seasonStorage: false,
      offerTyreCovers: false,
      agree: true,
      receiveNotifications: true,
    },
  });
  const watchAllFields = watch();

  useEffect(() => {
    if (watchAllFields) {
      setLegalEntity(watchAllFields?.legalEntity);
    }
  }, [watchAllFields?.legalEntity]);

  const onSubmit = data => {
    dispatch(orderActions.setContacts(data));
    dispatch(orderActions.setResultStep({ key: 'stepContacts', value: data }));
    dispatch(orderActions.nextStep());
  };

  return (
    <form className={styles.Form} onSubmit={handleSubmit(onSubmit)}>
      <div className={styles.GridForm}>
        <FormField
          label={'Имя'}
          name={'firstName'}
          type={'text'}
          register={register}
          value={watchAllFields.firstName}
          setValue={setValue}
          error={errors?.firstName?.message}
          isValid={Boolean(!errors?.firstName?.message) && Boolean(watchAllFields.firstName)}
          isRequired
          dataTestId="input-name"
        />
        <FormField
          label={'Фамилия'}
          name={'lastName'}
          type={'text'}
          register={register}
          value={watchAllFields.lastName}
          setValue={setValue}
          error={errors?.lastName?.message}
          isValid={Boolean(!errors?.lastName?.message) && Boolean(watchAllFields.lastName)}
          isRequired
          dataTestId="input-lastName"
        />
        <FormField
          id={'one_click_buy_id_PHONE'}
          label={'Телефон'}
          name={'phone'}
          type={'tel'}
          register={register}
          placeholder="+7 (___) ___-__-__"
          value={watchAllFields.phone}
          setValue={setValue}
          error={errors?.phone?.message}
          isValid={Boolean(!errors?.phone?.message) && Boolean(watchAllFields.phone)}
          isRequired
          dataTestId="input-phone"
        />
        <FormField
          id={'one_click_buy_id_EMAIL'}
          label={'Электронная почта'}
          name={'email'}
          type={'text'}
          register={register}
          value={watchAllFields.email}
          setValue={setValue}
          error={errors?.email?.message}
          isValid={Boolean(!errors?.email?.message) && Boolean(watchAllFields.email)}
          isRequired
          dataTestId="input-email"
        />
      </div>
      <FormField
        label={'Комментарий к заказу'}
        name={'comment'}
        type={'textarea'}
        register={register}
        classNameContainer={styles.Comment}
        value={watchAllFields.comment}
        setValue={setValue}
        error={errors?.comment?.message}
        isValid={Boolean(!errors?.comment?.message) && Boolean(watchAllFields.comment)}
        limit={150}
        dataTestId="input-comment"
      />
      {!shortRegion && (
        <CheckBox id={'legalEntity'} reg={register('legalEntity')}>
          <span>Я юридическое лицо</span>
        </CheckBox>
      )}
      {watchAllFields.legalEntity && (
        <div className={styles.GridForm}>
          <FormField
            label={'ИНН'}
            name={'inn'}
            type={'onlyNumbers'}
            maxLength={12}
            register={register}
            value={watchAllFields.inn}
            setValue={setValue}
            error={errors?.inn?.message}
            isValid={Boolean(!errors?.inn?.message) && Boolean(watchAllFields.inn)}
            placeholder={'Введите 10 или 12 цифр'}
            isRequired
            isRemovingSpaces
          />
          <FormField
            label={'Название организации'}
            name={'nameOrganization'}
            type={'text'}
            register={register}
            value={watchAllFields.nameOrganization}
            setValue={setValue}
            error={errors?.nameOrganization?.message}
            isValid={Boolean(!errors?.nameOrganization?.message) && Boolean(watchAllFields.nameOrganization)}
            placeholder={'Минимальное кол-во символов 3'}
            isRequired
          />
          <FormField
            label={'Юридический адрес организации'}
            name={'addressOrganization'}
            type={'text'}
            register={register}
            value={watchAllFields.addressOrganization}
            setValue={setValue}
            error={errors?.addressOrganization?.message}
            isValid={Boolean(!errors?.addressOrganization?.message) && Boolean(watchAllFields.addressOrganization)}
          />
          <FormField
            label={'БИК'}
            name={'bik'}
            type={'text'}
            register={register}
            value={watchAllFields.bik}
            setValue={setValue}
            error={errors?.bik?.message}
            isValid={Boolean(!errors?.bik?.message) && Boolean(watchAllFields.bik)}
            isRemovingSpaces
          />
          <FormField
            label={'Расчетный счет'}
            name={'checkingAccount'}
            type={'text'}
            register={register}
            value={watchAllFields.checkingAccount}
            setValue={setValue}
            error={errors?.checkingAccount?.message}
            isValid={Boolean(!errors?.checkingAccount?.message) && Boolean(watchAllFields.checkingAccount)}
          />
          <FormField
            label={'КПП'}
            name={'kpp'}
            type={'onlyNumbers'}
            maxLength={9}
            placeholder={watchAllFields.inn.length <= 10 && watchAllFields.inn.length !== 12 ? 'Введите 9 цифр' : ''}
            register={register}
            value={watchAllFields.kpp}
            setValue={setValue}
            error={errors?.kpp?.message}
            isRemovingSpaces
            isValid={Boolean(!errors?.kpp?.message) && Boolean(watchAllFields.kpp)}
            isRequired={watchAllFields.inn.length <= 10 && watchAllFields.inn.length !== 12}
          />
        </div>
      )}
      <div className={styles.AddServices}>
        <CheckBox id={'consultation'} reg={register('consultation')} data-testid="checkbox-manager-consultation">
          <span>Мне нужна консультация менеджера</span>
        </CheckBox>

        <CheckBox id={'record'} reg={register('record')} data-testid="checkbox-record-service">
          <span>Запишите меня на шиномонтаж</span>
        </CheckBox>

        <CheckBox id={'seasonStorage'} reg={register('seasonStorage')} data-testid="checkbox-season-storage">
          <span>Хочу оставить колеса на сезонное хранение</span>
        </CheckBox>
        {isShowCheckboxPackages && typeRegion === RegionTypes.Branch && (
          <CheckBox id={'offerTyreCovers'} reg={register('offerTyreCovers')} data-testid="checkbox-add-packages">
            <span>Добавить пакеты для шин (+{+regionId !== Region.Hab ? '25' : '50'} Р/шт.)</span>
          </CheckBox>
        )}
      </div>

      <div className={styles.GridCheckout}>
        <div>
          <CheckBoxAgree id={'agree-form-main-order'} reg={register('agree')} data-testid="checkbox-personal-information" />
          <CheckBoxReceiveNotifications id={'receive-notifications-form-main-order'} reg={register('receiveNotifications')} />
        </div>
        <Button disabled={!watch('agree') || !delivery} data-testid="btn-next">
          Далее
        </Button>
      </div>
    </form>
  );
};
